"use client"

// React imports
import React from "react"

// Next.js imports
import { useRouter } from "next/navigation"

// External SDK imports
import { useBetsSummary, useChain } from "@azuro-org/sdk"
import { Message } from "@locmod/intl"
import { openModal } from "@locmod/modal"
import { config, useWallet } from "wallet"
import { useBalance, useChainId, useSwitchChain } from "wagmi"

// Utility imports
import cx from "classnames"
import { constants, toLocaleString } from "helpers"
import { type Chain } from "viem"
import {
  arbitrum,
  base,
  baseSepolia,
  chiliz,
  gnosis,
  optimism,
  optimismSepolia,
  polygon,
  polygonAmoy,
  spicy,
} from "viem/chains"

// Internal hooks and utilities
import { useBalanceContext } from "src/contexts/BalanceContext"
import { useTokenSelection } from "@/contexts/TokenSelectionContext"
import { useUnredeemedOvertimeTickets } from "@/hooks"
import { useChainProvider } from "@/hooks/useChainProvider"

// Components
import { Icon } from "components/ui"
import { Dropdown } from "components/inputs"

// Local imports
import messages from "./messages"
import logger from "@/utils/logger"
import { NODE_ENV } from "@/config/env"
import { isOvertimeSupportedChain } from "@/utils/helper"

// Comprehensive chain configuration
const CHAIN_CONFIGS = [
  {
    id: base.id,
    name: "Base",
    currency: "USDC",
    icon: "networks/base",
    betTokenIcon: "currency/usdc",
    isTestnet: false,
    isAAWalletSupported: false,
  },
  {
    id: base.id,
    name: "Base",
    currency: "WETH",
    icon: "networks/base",
    betTokenIcon: "currency/weth",
    isTestnet: false,
    isAAWalletSupported: false,
  },
  {
    id: polygon.id,
    name: "Polygon",
    currency: "USDT",
    icon: "networks/polygon",
    betTokenIcon: "currency/usdt",
    isTestnet: false,
    isAAWalletSupported: true,
  },
  {
    id: optimism.id,
    name: "Optimism",
    currency: "USDC",
    icon: "networks/op",
    betTokenIcon: "currency/usdc",
    isTestnet: false,
    isAAWalletSupported: true,
  },
  {
    id: arbitrum.id, // Arbitrum
    name: "Arbitrum",
    currency: "USDC",
    icon: "networks/arbitrum",
    betTokenIcon: "currency/usdc",
    isTestnet: false,
    isAAWalletSupported: false,
  },
  {
    id: gnosis.id, // Gnosis
    name: "Gnosis",
    currency: "WXDAI",
    icon: "networks/gnosis",
    betTokenIcon: "currency/wxdai",
    isTestnet: false,
    isAAWalletSupported: false,
  },
  {
    id: chiliz.id, // Chiliz
    name: "Chiliz",
    currency: "WCHZ",
    icon: "networks/chiliz",
    betTokenIcon: "currency/wchz",
    isTestnet: false,
  },
  {
    id: spicy.id, // Spicy
    name: "Spicy",
    currency: "WCHZ",
    icon: "networks/spicy",
    betTokenIcon: "currency/wchz",
    isTestnet: true,
    isAAWalletSupported: false,
  },
  {
    id: polygonAmoy.id, // Polygon Amoy
    name: "Polygon Amoy",
    currency: "USDC",
    icon: "networks/polygon",
    betTokenIcon: "currency/usdc",
    isTestnet: true,
    isAAWalletSupported: false,
  },
  {
    id: optimismSepolia.id, // Optimism Sepolia
    name: "Optimism Sepolia",
    currency: "USDC",
    icon: "networks/op",
    betTokenIcon: "currency/usdc",
    isTestnet: true,
    isAAWalletSupported: false,
  },
]

type ChainConfig = {
  id: number
  name: string
  currency: string
  icon: string
  betTokenIcon: string
}

type ChainCurrencyProps = {
  className?: string
  chainClassName?: string
  chainId: any
  size: 4 | 5
  borderColor?: "accent-grey-900" | "accent-grey-1000" | "accent-grey-700"
  withGrayscale?: boolean
  currencyIcon?: string
}

const ChainCurrency: React.FC<ChainCurrencyProps> = React.memo(
  ({
    className,
    chainClassName,
    chainId,
    size,
    withGrayscale,
    currencyIcon,
  }) => {
    return (
      <div className={cx("flex items-center", className)}>
        <div
          className={cx(
            "border-2 rounded-full z-10 transition-colors",
            chainClassName
          )}
        >
          <Icon
            className={`size-${size}`}
            name={constants.chainIcons[chainId]}
          />
        </div>
        <Icon
          className={cx("-ml-1", `size-${size}`, { grayscale: withGrayscale })}
          name={currencyIcon || constants.currencyIcons[chainId]}
        />
      </div>
    )
  }
)

const ChainSelect: React.FC<{ close?: () => void; isAAWallet: boolean }> = ({
  close,
  isAAWallet,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { setAppChainId } = useChain()
  const router = useRouter()
  const { switchChain, chains } = useSwitchChain()

  const chainId = useChainId()
  const { selectedTokenIndex, setSelectedTokenIndex } = useTokenSelection()

  // Memoize the filtered chains to prevent unnecessary re-renders
  const filteredChains = React.useMemo(() => {
    // Don't render anything until the wallet is ready and we know the wallet type
    if (isAAWallet === undefined) {
      return []
    }

    // Get available chains from config
    const availableChains = config.chains.filter((chain: Chain) => {
      // For AA wallets, show only chains that support AA wallets
      if (isAAWallet) {
        const chainConfigs = CHAIN_CONFIGS.filter(
          config => config.id === chain.id && config.isAAWalletSupported
        )
        return chainConfigs.length > 0
      }
      return true
    })

    // Map to chain configs and flatten to handle multiple currencies per chain
    return availableChains.flatMap((chain: Chain) => {
      const chainConfigs = CHAIN_CONFIGS.filter(config => {
        // Only show testnet chains in development mode
        if (config.isTestnet && NODE_ENV !== "development") {
          return false
        }

        // For AA wallets, only show chains that support AA wallets
        if (isAAWallet && !config.isAAWalletSupported) {
          return false
        }

        return config.id === chain.id
      })

      return chainConfigs.map(config => ({
        ...chain,
        ...config,
      }))
    })
  }, [isAAWallet])

  /**
   * Optimized chain change handler with memoization and early returns
   *
   * @param chain - The chain to switch to
   * @performance Memoized with useCallback to prevent unnecessary re-renders
   */
  const handleChainChange = React.useCallback(
    async (chain: Chain & ChainConfig) => {
      const { id: chainId } = chain
      try {
        // Switch chain first (this is the main operation)
        await switchChain({ chainId: chainId as any })

        // Update app chain based on provider
        if (chainId === base.id) {
          // For Base chain, determine provider based on selected token
          if (chain.currency === "WETH") {
            // WETH = Azuro provider, update the SDK
            setAppChainId(chainId as any)
          }
          // USDC = Overtime provider, don't update SDK
        } else if (!isOvertimeSupportedChain(chainId)) {
          // For other non-Overtime chains, update the SDK
          setAppChainId(chainId as any)
        }

        if (chainId === base.id) {
          // USDC = 0 (Overtime), WETH = 5 (Azuro) for Base chain
          const newTokenIndex = chain.currency === "USDC" ? 0 : 5
          setSelectedTokenIndex(newTokenIndex)
        }

        // Close UI elements
        setIsOpen(false)
        close?.()

        router.push("/")
      } catch (error) {
        logger.error("Failed to switch chain:", error)

        openModal("SwitchNetworkModal", {
          chainId: chainId as any,
        })
      }
    },
    [switchChain, setAppChainId, close, setSelectedTokenIndex]
  )

  // Find the current chain config based on chainId and selected token for Base chain
  const currentChainConfig = React.useMemo(() => {
    if (chainId === base.id) {
      // For Base chain, find the config based on selected token
      const selectedTokenType = selectedTokenIndex === 0 ? "USDC" : "WETH"
      return CHAIN_CONFIGS.find(
        config => config.id === chainId && config.currency === selectedTokenType
      )
    }
    // For other chains, find the first config for this chain
    return CHAIN_CONFIGS.find(config => config.id === chainId)
  }, [chainId, selectedTokenIndex])

  return (
    <div className=" p-1 rounded-md">
      <button
        className="p-2 flex items-center justify-between w-full group/select"
        disabled={isAAWallet === undefined}
        onClick={() => isAAWallet !== undefined && setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <ChainCurrency
            className="mr-2"
            chainClassName="border-accent-grey-850"
            chainId={chainId}
            size={4}
            currencyIcon={currentChainConfig?.betTokenIcon}
          />
          <div className="text-caption-13">
            {currentChainConfig?.name} {currentChainConfig?.currency}
          </div>
        </div>
        {isAAWallet === undefined ? (
          <div className="size-4 animate-spin rounded-full border-2 border-accent-grey-700 border-t-accent-grey-200" />
        ) : (
          <Icon
            className={cx(
              "size-4 text-accent-grey-200 group-hover/select:text-accent-grey-30 transition-colors",
              {
                "rotate-180": isOpen,
              }
            )}
            name="interface/chevron_down"
          />
        )}
      </button>
      {isOpen && (
        <div className="w-full space-y-0.5">
          {filteredChains.map((chain: Chain & ChainConfig) => {
            // Check if this specific chain-currency combination is active
            const isActive =
              chainId === chain.id &&
              (chainId === base.id
                ? chain.currency ===
                  (selectedTokenIndex === 0 ? "USDC" : "WETH")
                : true) // For non-Base chains, just check chain ID

            return (
              <button
                key={`${chain.id}-${chain.currency}`}
                className={cx(
                  "flex items-center justify-between p-2 cursor-pointer bg-accent-grey-800 first-of-type:rounded-t-sm last-of-type:rounded-b-sm w-full"
                )}
                onClick={e => {
                  e.stopPropagation()
                  handleChainChange(chain)
                }}
              >
                <div className="flex items-center">
                  <ChainCurrency
                    chainId={chain.id as any}
                    chainClassName="border-accent-grey-800"
                    className="mr-2"
                    size={4}
                    currencyIcon={chain.betTokenIcon}
                  />
                  <div className="text-caption-12">
                    {chain.id === optimism.id ? "Optimism" : chain.name}{" "}
                    {chain.currency}
                  </div>
                </div>
                <div
                  className={cx(
                    "size-4 border flex items-center justify-center rounded-full",
                    {
                      "border-accent-grey-700": !isActive,
                      "border-brand-light-500": isActive,
                    }
                  )}
                >
                  {isActive && (
                    <div className="size-3 bg-brand-light-500 rounded-full" />
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const BalanceInfo: React.FC<{
  nativeBalanceData: NativeBalance
  isNativeBalanceFetching: boolean
  tokenBalanceData: NativeBalance
  isTokenBalanceFetching: boolean
}> = ({
  nativeBalanceData,
  isNativeBalanceFetching,
  tokenBalanceData,
  isTokenBalanceFetching,
}) => {
  const { account: address } = useWallet()
  const { isOvertimeChain, isAzuroChain } = useChainProvider()
  const { appChain } = useChain()

  const { data: betsSummaryData, isFetching: isBetsSummaryFetching } =
    useBetsSummary({
      account: address!,
      query: { enabled: isAzuroChain },
    })

  const {
    data: OvertimeBetsSummaryData,
    isFetching: OvertimeIsBetsSummaryFetching,
  } = useUnredeemedOvertimeTickets({
    address,
    enabled: isOvertimeChain,
  })

  // Determine the provider and return appropriate data
  const getProviderData = () => {
    switch (true) {
      case isOvertimeChain:
        return {
          betSummary: OvertimeBetsSummaryData,
          isFetching: OvertimeIsBetsSummaryFetching,
        }

      case isAzuroChain:
        return {
          betSummary: betsSummaryData,
          isFetching: isBetsSummaryFetching,
        }

      default:
        // Fallback to Azuro data
        return {
          betSummary: betsSummaryData,
          isFetching: isBetsSummaryFetching,
        }
    }
  }

  const { betSummary, isFetching } = getProviderData()

  const { inBets, toPayout } = betSummary || {}

  const tokenBalance = tokenBalanceData?.formatted
  const tokenSymbol = tokenBalanceData?.symbol

  const nativeBalance = nativeBalanceData?.formatted
  const nativeSymbol = nativeBalanceData?.symbol

  const formatedDecimals =
    appChain.id === base.id || appChain.id === baseSepolia.id ? 4 : 2

  return (
    <div className="rounded-md bg-accent-grey-900 overflow-hidden">
      <div className="py-2 px-3 border-b border-b-accent-grey-850">
        <Message
          className="text-caption-13 text-accent-grey-200 mb-[2px]"
          value={messages.balance}
        />
        <div className="space-x-1">
          {Boolean(isTokenBalanceFetching || isNativeBalanceFetching) ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <>
              <span className="text-caption-13 font-semibold">
                {toLocaleString(tokenBalance || 0, {
                  digits: formatedDecimals,
                })}{" "}
                {tokenSymbol}
              </span>
              <span className="text-caption-12 text-accent-grey-200">
                +{" "}
                {toLocaleString(nativeBalance || 0, {
                  digits: formatedDecimals,
                })}{" "}
                {nativeSymbol}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center">
        <div className="w-full py-2 px-3">
          <Message
            className="text-caption-13 text-accent-grey-200 mb-[2px]"
            value={messages.inBets}
            tag="p"
          />
          {isFetching ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <div className="text-caption-13 font-semibold">
              {toLocaleString(inBets || 0, { digits: formatedDecimals })}{" "}
              {tokenSymbol}
            </div>
          )}
        </div>
        <div className="w-full border-l border-l-accent-grey-850 py-2 px-3">
          <Message
            className="text-caption-13 text-accent-grey-200 mb-[2px]"
            value={messages.toRedeem}
            tag="p"
          />
          {isFetching ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <div className="text-caption-13 font-semibold">
              {toLocaleString(toPayout || 0, { digits: formatedDecimals })}{" "}
              {tokenSymbol}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const Content: React.FC<{
  close?: () => void
  nativeBalanceData: NativeBalance
  isNativeBalanceFetching: boolean
  tokenBalanceData: NativeBalance
  isTokenBalanceFetching: boolean
  isAAWallet: boolean
}> = ({
  close,
  nativeBalanceData,
  isNativeBalanceFetching,
  tokenBalanceData,
  isTokenBalanceFetching,
  isAAWallet,
}) => {
  return (
    <div className=" p-2 ds:w-[18.75rem] bg-accent-grey-850 rounded-md overflow-hidden space-y-2">
      <ChainSelect close={close} isAAWallet={isAAWallet} />

      <BalanceInfo
        nativeBalanceData={nativeBalanceData}
        isNativeBalanceFetching={isNativeBalanceFetching}
        tokenBalanceData={tokenBalanceData}
        isTokenBalanceFetching={isTokenBalanceFetching}
      />
    </div>
  )
}

type NativeBalance = {
  value: bigint
  decimals: number
  symbol: string
  formatted: string
}

const Balance = () => {
  const { account, isAAWallet } = useWallet()
  const { appChain, betToken } = useChain()
  const { setRefetchFunction } = useBalanceContext()

  const { chains } = useSwitchChain()
  const chainId = useChainId()

  // Use shared token selection context
  const { selectedTokenIndex } = useTokenSelection()
  const {
    tokens,
    defaultToken,
    isOvertimeChain,
    isAzuroChain,
    getTokenByIndex,
  } = useChainProvider()

  const {
    data: nativeBalanceData,
    isFetching: isNativeBalanceFetching,
    refetch: refetchNativeBalance,
  } = useBalance({
    address: account,
  }) as {
    data: NativeBalance
    isFetching: boolean
    refetch: () => void
  }

  const formatedDecimals =
    appChain.id === base.id || appChain.id === baseSepolia.id ? 4 : 2

  // Get token address based on provider and selection
  const tokenAddress = React.useMemo(() => {
    if (chainId === base.id && selectedTokenIndex === 5 && isOvertimeChain) {
      // For Base chain, use the selected token (USDC or WETH)
      return (
        (getTokenByIndex(selectedTokenIndex)?.address as `0x${string}`) ||
        undefined
      )
    } else if (isOvertimeChain) {
      // For other Overtime chains, use the default token
      return defaultToken?.address as `0x${string}` | undefined
    } else {
      // For Azuro chains, use the bet token
      return betToken.address
    }
  }, [
    chainId,
    isOvertimeChain,
    selectedTokenIndex,
    defaultToken,
    betToken.address,
    getTokenByIndex,
  ])

  const {
    data: balanceData,
    isFetching: isBalanceFetching,
    refetch: refetchTokenBalance,
  } = useBalance({
    address: account,
    token: tokenAddress,
  })

  // Register refetch functions with the context
  React.useEffect(() => {
    const combinedRefetch = () => {
      refetchNativeBalance()
      refetchTokenBalance()
    }
    setRefetchFunction(combinedRefetch)
  }, [refetchNativeBalance, refetchTokenBalance, setRefetchFunction])

  const tokenBalance = balanceData?.formatted
  const handleGetTokensClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    openModal("FundingModal")
  }

  const rootClassName = cx(
    "wd:h-10 -wd:h-8 bg-accent-grey-850 flex items-center justify-between border border-accent-grey-800 px-3 w-fit text-accent-grey-200 ui-open:text-accent-grey-30 hover:text-accent-grey-30 ui-open:bg-accent-grey-850 hover:bg-accent-grey-850 ui-open:border-grey-20 hover:border-grey-20 transition-all mb:w-full rounded-md"
  )

  const currentChain = chains.find(chain => chain.id === chainId)

  // Get the appropriate currency icon for the current chain and token selection
  const getCurrentCurrencyIcon = () => {
    if (chainId === 8453) {
      // For Base chain, use the selected token's currency icon
      const currentTokenType = selectedTokenIndex === 0 ? "USDC" : "WETH"
      const chainConfig = CHAIN_CONFIGS.find(
        config => config.id === chainId && config.currency === currentTokenType
      )
      return chainConfig?.betTokenIcon
    }
    // For other chains, use the default currency icon
    return constants.currencyIcons[chainId as any]
  }

  return (
    <Dropdown
      className={cx("group")}
      contentClassName="mb:p-0"
      content={({ close }) => (
        <Content
          close={close}
          nativeBalanceData={nativeBalanceData}
          isNativeBalanceFetching={isNativeBalanceFetching}
          tokenBalanceData={balanceData as NativeBalance}
          isTokenBalanceFetching={isBalanceFetching}
          isAAWallet={isAAWallet}
        />
      )}
      placement="bottomRight"
    >
      <div className={rootClassName}>
        <div className="flex items-center">
          <ChainCurrency
            className="mr-2"
            chainClassName="border-accent-grey-900 group-hover:border-grey-10 ui-open:border-grey-10"
            chainId={currentChain?.id}
            size={5}
            currencyIcon={getCurrentCurrencyIcon()}
          />
          {isBalanceFetching ? (
            <div className="bone h-4 w-10 rounded-full" />
          ) : (
            <div className="text-caption-13">
              {toLocaleString(tokenBalance || 0, { digits: formatedDecimals })}
            </div>
          )}
          <Icon
            className="size-4 ui-open:rotate-180"
            name="interface/caret_down"
          />
        </div>
        <div
          className="flex items-center justify-center size-6 rounded-min bg-brand-light-500 ml-2 cursor-pointer text-accent-grey-900 flex-none"
          onClick={handleGetTokensClick}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
        >
          <Icon className="size-4" name="interface/plus" />
        </div>
      </div>
    </Dropdown>
  )
}

export default Balance
